import { useState } from "react";
import { toast } from "sonner";
import { PlusCircle } from "lucide-react";
import { Button } from "./ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./ui/dialog";
import { api, errMsg, fmtDT } from "../lib/api";
import { StatusBadge, Empty, useLoad } from "./common";

export function RequestMoreButton({ onDone, label = "Request more leads", approver = "the admin", scope = null, testid = "request-more-leads-btn" }) {
  const [open, setOpen] = useState(false);
  const [f, setF] = useState({ quantity: "", reason: "" });
  const [sending, setSending] = useState(false);
  const send = async () => {
    setSending(true);
    try {
      await api.post("/extra-requests", { quantity: Number(f.quantity), reason: f.reason || null, scope });
      toast.success(`Sent to ${approver} for approval`); setOpen(false); setF({ quantity: "", reason: "" }); onDone?.();
    } catch (e) { toast.error(errMsg(e)); }
    setSending(false);
  };
  return (
    <>
      <Button variant="outline" className="h-10" onClick={() => setOpen(true)} data-testid={testid}><PlusCircle className="w-4 h-4 mr-2" />{label}</Button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-white" data-testid="request-more-dialog">
          <DialogHeader><DialogTitle>{label}</DialogTitle></DialogHeader>
          <p className="text-sm text-zinc-600">Leads above your daily cap need approval from {approver}. They'll get an email and you'll be emailed the decision.</p>
          <input type="number" min="1" className="fld w-full mono" placeholder="How many leads" value={f.quantity} onChange={(e) => setF({ ...f, quantity: e.target.value })} data-testid="request-more-qty" />
          <textarea className="fld w-full h-20 py-2" placeholder="Reason (optional)" value={f.reason} onChange={(e) => setF({ ...f, reason: e.target.value })} data-testid="request-more-reason" />
          <Button onClick={send} disabled={sending || !(Number(f.quantity) > 0)} data-testid="request-more-submit">Send for approval</Button>
        </DialogContent>
      </Dialog>
    </>
  );
}

export function MyExtraRequests({ items }) {
  if (!items?.length) return null;
  return (
    <div className="panel overflow-x-auto" data-testid="my-extra-requests">
      <table className="dt"><thead><tr><th>Sent</th><th className="num">Asked</th><th className="num">Approved</th><th className="num">Delivered</th><th>Status</th><th>Note</th></tr></thead>
        <tbody>{items.map((x) => <tr key={x.id}><td className="mono text-xs">{fmtDT(x.created_at)}</td><td className="num">{x.quantity}</td><td className="num">{x.approved_quantity ?? "—"}</td><td className="num">{x.delivered ?? "—"}</td><td><StatusBadge s={x.status === "approved" ? "fulfilled" : x.status} label={x.status === "pending" ? `waiting for ${x.approver_role === "leader" ? "team leader" : "admin"}` : x.status} /></td><td className="text-xs">{x.note || ""}</td></tr>)}</tbody></table>
    </div>
  );
}

export function AdminExtraQueue({ onChange, filter, emptyText = "No requests for extra leads." }) {
  const [all, reload] = useLoad(async () => (await api.get("/extra-requests")).data);
  const items = filter ? (all || []).filter(filter) : all;
  const [qty, setQty] = useState({});
  const act = async (x, action) => {
    try {
      const note = action === "reject" ? window.prompt("Reason for rejecting (optional)", "") : null;
      if (action === "reject" && note === null) return;
      await api.post(`/extra-requests/${x.id}/${action}`, { quantity: qty[x.id] ? Number(qty[x.id]) : null, note });
      toast.success(action === "approve" ? "Approved and delivered" : "Rejected"); reload(); onChange?.();
    } catch (e) { toast.error(errMsg(e)); reload(); }
  };
  if (!items?.length) return <Empty text={emptyText} />;
  return (
    <div className="overflow-x-auto">
      <table className="dt" data-testid="admin-extra-queue"><thead><tr><th>From</th><th>For</th><th>Team</th><th>Approver</th><th>Sent</th><th className="num">Asked</th><th>Reason</th><th>Status</th><th className="num">Delivered</th><th /></tr></thead>
        <tbody>{items.map((x) => (
          <tr key={x.id} data-testid={`extra-row-${x.id}`}>
            <td className="font-medium">{x.requester_name}</td><td className="text-xs">{x.kind === "team" ? "whole team" : "themselves"}</td><td className="text-xs">{x.team_name}</td><td className="text-xs">{x.approver_role === "leader" ? "team leader" : "admin"}</td>
            <td className="mono text-xs">{fmtDT(x.created_at)}</td><td className="num">{x.quantity}</td><td className="text-xs max-w-[200px]">{x.reason || ""}</td>
            <td><StatusBadge s={x.status === "approved" ? "fulfilled" : x.status} testid={`extra-status-${x.id}`} />{x.note && <div className="text-[10px] text-amber-700">{x.note}</div>}</td>
            <td className="num">{x.delivered ?? "—"}</td>
            <td className="text-right whitespace-nowrap space-x-1">{x.status === "pending" && x.can_decide && <>
              <input type="number" min="1" placeholder={x.quantity} className="fld w-24 h-8 mono" value={qty[x.id] || ""} onChange={(e) => setQty({ ...qty, [x.id]: e.target.value })} data-testid={`extra-qty-${x.id}`} />
              <Button size="sm" onClick={() => act(x, "approve")} data-testid={`extra-approve-${x.id}`}>Approve</Button>
              <Button size="sm" variant="ghost" onClick={() => act(x, "reject")} data-testid={`extra-reject-${x.id}`}>Reject</Button></>}</td>
          </tr>))}</tbody></table>
    </div>
  );
}
